import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import { FaBars, FaUpload, FaChevronDown, FaEye, FaEyeSlash, FaUser, FaCog, FaChartLine, FaCalendarAlt } from 'react-icons/fa';
import { supabase } from './supabaseClient';
import PageHeader from './components/PageHeader';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getStudentBookingRequests } from './bookingService';
import { awardProfileCompletionBonus } from './xpService';
import LoadingSpinner from './components/LoadingSpinner';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from './utils/cropImage';
import LocationPicker from './components/location/LocationPicker';

const bahrainUniversities = [
  "University of Bahrain",
  "Arabian Gulf University",
  "Ahlia University",
  "Bahrain Polytechnic",
  "Royal University for Women",
  "Applied Science University",
  "Gulf University",
  "AMA International University",
  "Bahrain Institute of Banking and Finance",
  "Other"
];

const bahrainSchools = [
  "Al Hekma International School",
  "St. Christopher's School",
  "The Indian School",
  "Bahrain Bayan School",
  "Naseem International School",
  "Al Noor International School",
  "New Millennium School",
  "British School of Bahrain",
  "Shaikha Hessa Girls' School",
  "Al Wisam School",
  "Al Raja School",
  "Al Rawabi Private School",
  "Al Mahd Day Boarding School",
  "Al Salam School",
  "Al Shorooq International School",
  "Al-Amal School",
  "Al-Ibdaa School",
  "Al-Mawakeb School",
  "Al-Muntazah School",
  "Al-Taqwa School",
  "Al-Waha School",
  "Al-Wurood International School",
  "Al-Zahra School",
  "Beacon Private School",
  "Capital School Bahrain",
  "City International School",
  "Diyar International Private School",
  "Dream International School",
  "Future Generation Private School",
  "GEMS International School",
  "Indian School Bahrain",
  "Iqra'a Bilingual School",
  "Khalil Bin Ahmed School",
  "Kuwait National English School",
  "New Indian School",
  "NIVA International School",
  "Pakistan Urdu School",
  "Riffa Views International School",
  "Saar International School",
  "Sacred Heart School",
  "St. Michael's School",
  "The Asian School",
  "The British School of Bahrain",
  "The French School of Bahrain",
  "The Japanese School in Bahrain",
  "The New Horizon School",
  "The Philippine School Bahrain",
  "The Sheikh Khalifa Bin Salman Institute of Technology",
  "The Westminster School",
  "Nasser Centre for Science and Technology",
  "Abdulla Bin Salman Primary Boys School",
  "Abdulla Bin Salman Primary Girls School",
  "Abu Saiba Primary Boys School",
  "Abu Saiba Primary Girls School",
  "Adhari Primary Boys School",
  "Adhari Primary Girls School",
  "Al Hidd Secondary Boys School",
  "Al Hidd Secondary Girls School",
  "Al Iman Primary Boys School",
  "Al Iman Primary Girls School",
  "Al Ma'arifa Secondary Boys School",
  "Al Ma'arifa Secondary Girls School",
  "Al Malkiya Primary Boys School",
  "Al Malkiya Primary Girls School",
  "Al Qadam Primary Boys School",
  "Al Qadam Primary Girls School",
  "Al Shurooq Secondary Girls School",
  "Al Wusta Primary Boys School",
  "Al Wusta Primary Girls School",
  "Arad Primary Boys School",
  "Arad Primary Girls School",
  "Bani Jamra Primary Boys School",
  "Bani Jamra Primary Girls School",
  "Budaiya Primary Boys School",
  "Budaiya Primary Girls School",
  "Duraz Primary Boys School",
  "Duraz Primary Girls School",
  "East Riffa Secondary Boys School",
  "East Riffa Secondary Girls School",
  "Isa Town Secondary Boys School",
  "Isa Town Secondary Girls School",
  "Jidhafs Secondary Boys School",
  "Jidhafs Secondary Girls School",
  "Juffair Primary Boys School",
  "Juffair Primary Girls School",
  "Manama Secondary Boys School",
  "Manama Secondary Girls School",
  "Muharraq Secondary Boys School",
  "Muharraq Secondary Girls School",
  "Riffa Secondary Boys School",
  "Riffa Secondary Girls School",
  "Sitra Secondary Boys School",
  "Sitra Secondary Girls School",
  "Tubli Primary Boys School",
  "Tubli Primary Girls School",
  "Other"
];

const schoolGrades = Array.from({ length: 12 }, (_, i) => `Grade ${i + 1}`);
const universityGrades = ['Foundation', 'Diploma', 'Bachelor', 'Master', 'PhD'];

export default function ProfilePage() {
  const { sidebarVisible, setSidebarVisible, isArabic, refreshUserProfile } = useOutletContext() || {};
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'personal', 'settings'
  const [institutionType, setInstitutionType] = useState(''); // 'school' or 'university'
  const [customInstitution, setCustomInstitution] = useState(''); // For "Other" option
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    grade: '',
    profile_photo: null,
    preferred_location_name: '',
    preferred_location_latitude: null,
    preferred_location_longitude: null,
    preferred_location_place_id: null,
    preferred_location_formatted_address: ''
  });
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [stats, setStats] = useState({
    totalSessions: 0,
    upcomingSessions: 0,
    completedSessions: 0,
    totalSpent: 0,
    pendingRequests: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isEditingPersonalInfo, setIsEditingPersonalInfo] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate profile completeness
  const calculateCompleteness = () => {
    const isFieldComplete = (value) => {
      if (!value) return false;
      const str = String(value).trim();
      return str !== '' && str !== 'Not provided' && str !== 'غير متوفر';
    };
    
    // Check each field individually with proper validation
    const nameComplete = isFieldComplete(profile.name);
    const emailComplete = isFieldComplete(profile.email) && profile.email.includes('@'); // Valid email check
    const phoneComplete = isFieldComplete(profile.phone);
    const schoolComplete = isFieldComplete(profile.school) && profile.school !== 'Other';
    const gradeComplete = isFieldComplete(profile.grade);
    const photoComplete = !!(imagePreview || profile.image);
    
    const fields = [
      nameComplete,
      emailComplete,
      phoneComplete,
      schoolComplete,
      gradeComplete,
      photoComplete
    ];
    
    const completed = fields.filter(Boolean).length;
    const percentage = Math.round((completed / fields.length) * 100);
    
    return percentage;
  };

  const completeness = calculateCompleteness();

  // Fetch statistics
  useEffect(() => {
    async function fetchStats() {
      try {
        setLoadingStats(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const studentEmail = user.email;
        const { data: bookings } = await getStudentBookingRequests(studentEmail);
        
        if (bookings) {
          const totalSessions = bookings.filter(b => b.status === 'accepted').length;
          const today = new Date().toISOString().split('T')[0];
          const upcomingSessions = bookings.filter(b => 
            b.status === 'accepted' && b.date >= today
          ).length;
          const completedSessions = bookings.filter(b => 
            b.status === 'accepted' && b.date < today
          ).length;
          const pendingRequests = bookings.filter(b => b.status === 'pending').length;
          const totalSpent = bookings
            .filter(b => b.status === 'accepted')
            .reduce((sum, b) => sum + (parseFloat(b.total_price) || 0), 0);

          setStats({
            totalSessions,
            upcomingSessions,
            completedSessions,
            pendingRequests,
            totalSpent
          });
        }
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoadingStats(false);
      }
    }
    fetchStats();
  }, []);

  useEffect(() => {
    setLoading(true);
    const getUserAndProfile = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      const user = session?.user;
      if (user) {
        const meta = user.user_metadata || user.raw_user_meta_data || {};
        const { data: student } = await supabase
          .from('students')
          .select('*')
          .eq('email', user.email)
          .single();

        const studentSchool = student?.school || '';
        let schoolValue = studentSchool;

        // Determine institution type based on existing school
        if (studentSchool) {
          if (bahrainUniversities.includes(studentSchool)) {
            setInstitutionType('university');
          } else if (bahrainSchools.includes(studentSchool)) {
            setInstitutionType('school');
          } else if (studentSchool && studentSchool !== '') {
            // Custom institution - determine type based on grade
            const grade = student?.grade_levels || '';
            if (universityGrades.includes(grade)) {
              setInstitutionType('university');
            } else {
              setInstitutionType('school');
            }
            setCustomInstitution(studentSchool);
            schoolValue = 'Other'; // Set dropdown to "Other"
          }
        }

        const nameValue = meta.full_name || student?.full_name || 'Not provided';
        const phoneValue = meta.phone || student?.phone || 'Not provided';
        
        setProfile({
          name: nameValue,
          email: user.email || 'Not provided',
          phone: phoneValue,
          school: schoolValue,
          grade: student?.grade_levels || '',
          image: student?.profile_photo || null,
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
          preferred_location_name: student?.preferred_location_name || '',
          preferred_location_latitude: student?.preferred_location_latitude || null,
          preferred_location_longitude: student?.preferred_location_longitude || null,
          preferred_location_place_id: student?.preferred_location_place_id || null,
          preferred_location_formatted_address: student?.preferred_location_formatted_address || ''
        });
        
        // Initialize edited values (remove "Not provided" placeholder)
        setEditedName(nameValue === 'Not provided' || nameValue === 'غير متوفر' ? '' : nameValue);
        setEditedPhone(phoneValue === 'Not provided' || phoneValue === 'غير متوفر' ? '' : phoneValue);
        
        if (student?.profile_photo) {
          setImagePreview(student.profile_photo);
        } else {
          setImagePreview(null);
        }
      } else {
        setProfile({ name: '', email: '', phone: '' });
        setImagePreview(null);
      }
      setLoading(false);
    };
    getUserAndProfile();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    
    // Create image URL for cropping
    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);
    setShowCropModal(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropComplete = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated.');
        setLoading(false);
        return;
      }

      // Get cropped image
      const croppedImageUrl = await getCroppedImg(imageToCrop, croppedAreaPixels);
      if (!croppedImageUrl) {
        setError('Failed to crop image.');
        setLoading(false);
        return;
      }

      // Convert blob URL to file
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], `profile-${user.id}-${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Upload to Supabase
      const filePath = `user-profiles/${user.id}/${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, { upsert: true });
      
      if (uploadError) {
        setError('Failed to upload image to Supabase Storage.');
        setLoading(false);
        return;
      }

      const { data: publicUrlData } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);
      const publicUrl = publicUrlData?.publicUrl;
      
      if (!publicUrl) {
        setError('Failed to get public URL for image.');
        setLoading(false);
        return;
      }

      setImagePreview(publicUrl);
      
      // Try UPDATE first (record should exist)
      const { data: updateData, error: updateError } = await supabase
        .from('students')
        .update({ profile_photo: publicUrl })
        .eq('email', user.email)
        .select()
        .single();

      if (updateError) {
        console.error('Profile photo update error:', updateError);
        
        // If update failed because record doesn't exist, try INSERT
        if (updateError.code === 'PGRST116' || updateError.message?.includes('No rows')) {
          console.log('Student record not found, attempting to create...');
          
          // Get full_name from user metadata or profile state
          const fullName = user.user_metadata?.full_name || 
                           profile.name || 
                           user.email?.split('@')[0] || 
                           'Student';
          
          const { data: insertData, error: insertError } = await supabase
            .from('students')
            .insert({
              email: user.email,
              uuid_id: user.id,
              profile_photo: publicUrl,
              full_name: fullName
            })
            .select()
            .single();
          
          if (insertError) {
            console.error('Failed to create student record:', insertError);
            setError(`Image uploaded, but failed to save URL to profile: ${insertError.message}`);
            setLoading(false);
            return;
          }
          
          console.log('Student record created successfully');
          setProfile(prev => ({ ...prev, image: publicUrl }));
          
          // Refresh the cached user profile in the layout so it updates everywhere
          if (refreshUserProfile && typeof refreshUserProfile === 'function') {
            console.log('🔄 Refreshing cached user profile after image update...');
            refreshUserProfile();
          }
          
          setSuccess('Profile picture updated!');
        } else {
          setError(`Image uploaded, but failed to save URL to profile: ${updateError.message}`);
          setLoading(false);
          return;
        }
      } else if (updateData) {
        // Update succeeded
        console.log('Profile photo updated successfully:', updateData);
        setProfile(prev => ({ ...prev, image: publicUrl }));
        
        // Refresh the cached user profile in the layout so it updates everywhere
        if (refreshUserProfile && typeof refreshUserProfile === 'function') {
          console.log('🔄 Refreshing cached user profile after image update...');
          refreshUserProfile();
        }
        
        setSuccess('Profile picture updated!');
      } else {
        // This shouldn't happen, but handle it gracefully
        console.warn('Update succeeded but no data returned');
        setProfile(prev => ({ ...prev, image: publicUrl }));
        
        // Refresh the cached user profile in the layout so it updates everywhere
        if (refreshUserProfile && typeof refreshUserProfile === 'function') {
          console.log('🔄 Refreshing cached user profile after image update...');
          refreshUserProfile();
        }
        
        setSuccess('Profile picture updated!');
      }

      // Clean up only after successful update
      URL.revokeObjectURL(imageToCrop);
      URL.revokeObjectURL(croppedImageUrl);
      setShowCropModal(false);
      setImageToCrop(null);
      setLoading(false);
    } catch (err) {
      console.error('Error cropping/uploading image:', err);
      setError(`Failed to upload image: ${err.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  const handleCropCancel = () => {
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
    }
    setShowCropModal(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!window.confirm(isArabic 
      ? 'هل أنت متأكد من حذف صورة الملف الشخصي؟' 
      : 'Are you sure you want to delete your profile picture?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated.');
        setLoading(false);
        return;
      }

      // Update student profile to remove photo
      const { data: updateData, error: updateError } = await supabase
        .from('students')
        .update({ profile_photo: null })
        .eq('email', user.email)
        .select()
        .single();

      if (updateError) {
        console.error('Failed to delete profile photo:', updateError);
        setError('Failed to delete profile picture.');
        setLoading(false);
        return;
      }

      // Clear local state
      setProfile(prev => ({ ...prev, image: null }));
      setImagePreview(null);
      
      // Refresh the cached user profile in the layout so it updates everywhere
      if (refreshUserProfile && typeof refreshUserProfile === 'function') {
        console.log('🔄 Refreshing cached user profile after image deletion...');
        refreshUserProfile();
      }
      
      setSuccess(isArabic ? 'تم حذف صورة الملف الشخصي' : 'Profile picture deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting profile picture:', err);
      setError('Failed to delete profile picture.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });

    // If school is changed and it's not "Other", clear custom institution
    if (name === 'school' && value !== 'Other') {
      setCustomInstitution('');
    }
  };

  const handleInstitutionTypeChange = (e) => {
    const newType = e.target.value;
    setInstitutionType(newType);
    // Reset school, grade and custom institution when changing institution type
    setProfile({ ...profile, school: '', grade: '' });
    setCustomInstitution('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');

    // Validate that custom institution is provided if "Other" is selected
    if (profile.school === 'Other' && !customInstitution.trim()) {
      setError(isArabic
        ? 'يرجى إدخال اسم المؤسسة التعليمية'
        : 'Please enter the institution name');
      setLoading(false);
      return;
    }

    try {
      const email = localStorage.getItem('userEmail');

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Use custom institution name if "Other" is selected
      const schoolToSave = profile.school === 'Other' && customInstitution.trim()
        ? customInstitution.trim()
        : profile.school;

      // Debug logging
      console.log('📊 Profile update:', {
        newSchool: schoolToSave,
        newGrade: profile.grade,
        userId: user?.id,
        userEmail: user?.email
      });

      const updateData = {
        school: schoolToSave,
        grade_levels: profile.grade
      };

      // Add name and phone if they were edited
      if (isEditingPersonalInfo) {
        if (editedName.trim()) {
          updateData.full_name = editedName.trim();
        } else {
          updateData.full_name = null; // Set to null if blank
        }
        
        if (editedPhone.trim()) {
          updateData.phone = editedPhone.trim();
        } else {
          updateData.phone = null; // Set to null if blank
        }
      }

      if (profile.image !== undefined) {
        updateData.profile_photo = profile.image;
      }

      // Add location fields
      updateData.preferred_location_name = profile.preferred_location_name || null;
      updateData.preferred_location_latitude = profile.preferred_location_latitude;
      updateData.preferred_location_longitude = profile.preferred_location_longitude;
      updateData.preferred_location_place_id = profile.preferred_location_place_id || null;
      updateData.preferred_location_formatted_address = profile.preferred_location_formatted_address || null;

      // Update students table
      const { data, error } = await supabase.from('students').update(updateData).eq('email', email);

      if (error) {
        console.error('Error updating students table:', error);
        setError('Failed to update profile.');
        setLoading(false);
        return;
      }

      // Also update user metadata if name or phone were edited
      if (isEditingPersonalInfo && (editedName.trim() || editedPhone.trim())) {
        const metadataUpdate = {};
        if (editedName.trim()) {
          metadataUpdate.full_name = editedName.trim();
        }
        if (editedPhone.trim()) {
          metadataUpdate.phone = editedPhone.trim();
        }
        
        if (Object.keys(metadataUpdate).length > 0) {
          const { error: metadataError } = await supabase.auth.updateUser({
            data: metadataUpdate
          });
          
          if (metadataError) {
            console.error('Error updating user metadata:', metadataError);
            // Don't fail the whole update, just log it
          }
        }
      }

      // Refetch the updated profile data
      const { data: updatedStudent } = await supabase
        .from('students')
        .select('*')
        .eq('email', email)
        .single();

      const { data: { user: updatedUser } } = await supabase.auth.getUser();
      const updatedMeta = updatedUser?.user_metadata || {};

      // Update local profile state with fresh data
      const updatedName = updatedMeta.full_name || updatedStudent?.full_name || 'Not provided';
      const updatedPhone = updatedMeta.phone || updatedStudent?.phone || 'Not provided';
      
      setProfile(prev => ({
        ...prev,
        name: updatedName,
        phone: updatedPhone,
        school: updatedStudent?.school || prev.school,
        grade: updatedStudent?.grade_levels || prev.grade,
        image: updatedStudent?.profile_photo || prev.image
      }));

      // Update edited values for next edit
      setEditedName(updatedName === 'Not provided' || updatedName === 'غير متوفر' ? '' : updatedName);
      setEditedPhone(updatedPhone === 'Not provided' || updatedPhone === 'غير متوفر' ? '' : updatedPhone);
      
      if (isEditingPersonalInfo) {
        setIsEditingPersonalInfo(false);
      }
      
      // Refresh the cached user profile in the layout so it updates everywhere (Sidebar, Dashboard, etc.)
      if (refreshUserProfile && typeof refreshUserProfile === 'function') {
        console.log('🔄 Refreshing cached user profile...');
        refreshUserProfile();
      }
        
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);

      // Try to award profile completion bonus if profile is now complete
      // Backend will check if it was already awarded (duplicate prevention)
      const updatedSchool = updatedStudent?.school || profile.school;
      const updatedGrade = updatedStudent?.grade_levels || profile.grade;
      const isProfileComplete = updatedSchool && updatedGrade && updatedSchool !== 'Other';

      if (isProfileComplete && user) {
        console.log('🎯 Profile complete! Attempting to award 20 XP bonus...');
        awardProfileCompletionBonus(user.id, user.email)
          .then(({ data, error }) => {
            if (data && !error) {
              console.log('✅ Profile completion bonus awarded:', data);
              setSuccess('Profile updated successfully! +20 XP earned!');
            } else if (error) {
              console.log('ℹ️ Profile completion bonus not awarded:', error);
              // Don't show error to user - they might have already received it
            }
          })
          .catch(err => {
            console.log('ℹ️ Profile completion bonus exception:', err);
            // Non-critical - don't show error to user
          });
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setProfile({ name: '', email: '', phone: '', school: '', grade: '', image: null });
    setImagePreview(null);
    localStorage.clear();
    navigate('/signin');
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // Get user session and token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('User not authenticated.');
        setLoading(false);
        return;
      }

      const token = session.access_token;

      // Call backend API to delete account
      const response = await fetch('http://localhost:4001/api/delete-account', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('Account deletion failed:', result.error);
        setError(result.error || 'Failed to delete account.');
        setLoading(false);
        return;
      }

      console.log('✅ Account deleted successfully');

      // Sign out and clear local storage
      await supabase.auth.signOut();
      localStorage.clear();

      // Redirect to home page
      navigate('/');
    } catch (err) {
      console.error('Delete account error:', err);
      setError('Failed to delete account. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');
    if (!profile.oldPassword || !profile.newPassword || !profile.confirmPassword) {
      setError('Please fill in all password fields.');
      setLoading(false);
      return;
    }
    if (profile.newPassword !== profile.confirmPassword) {
      setError('New passwords do not match.');
      setLoading(false);
      return;
    }
    try {
      const email = profile.email;
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: profile.oldPassword,
      });
      if (signInError || !data.user) {
        setError('Old password is incorrect.');
        setLoading(false);
        return;
      }
      const { error: updateError } = await supabase.auth.updateUser({
        password: profile.newPassword,
      });
      if (updateError) {
        setError('Failed to update password.');
      } else {
        setSuccess('Password updated successfully!');
        setProfile(prev => ({ ...prev, oldPassword: '', newPassword: '', confirmPassword: '' }));
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !profile.name) {
    return (
      <main style={{ flex: 1, padding: '40px 48px', minHeight: '100vh', background: '#f7f9fb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingSpinner size={80} loadingMessage={isArabic ? 'جاري تحميل الملف الشخصي...' : 'Loading profile...'} isArabic={isArabic} />
        </div>
      </main>
    );
  }

  // Arabic translations
  const t = isArabic
    ? {
        profile: 'الملف الشخصي',
        subtitle: 'إدارة معلوماتك الشخصية والإعدادات',
        overview: 'نظرة عامة',
        personal: 'المعلومات الشخصية',
        settings: 'الإعدادات',
        completeness: 'اكتمال الملف الشخصي',
        name: 'الاسم',
        email: 'البريد الإلكتروني',
        phone: 'رقم الهاتف',
        school: 'المدرسة أو الجامعة',
        grade: 'المستوى الدراسي',
        changePassword: 'تغيير كلمة المرور',
        oldPassword: 'كلمة المرور القديمة',
        newPassword: 'كلمة المرور الجديدة',
        confirmPassword: 'تأكيد كلمة المرور',
        logout: 'تسجيل الخروج',
        deleteAccount: 'حذف الحساب',
        deleteAccountWarning: 'سيؤدي هذا إلى حذف حسابك وجميع بياناتك بشكل دائم',
        deleteConfirmTitle: 'هل أنت متأكد؟',
        deleteConfirmMessage: 'هذا الإجراء لا يمكن التراجع عنه. سيتم حذف حسابك وجميع بياناتك بشكل دائم.',
        cancel: 'إلغاء',
        confirmDelete: 'نعم، احذف حسابي',
        updateChanges: 'تحديث التغييرات',
        totalSessions: 'إجمالي الجلسات',
        upcomingSessions: 'الجلسات القادمة',
        completedSessions: 'الجلسات المكتملة',
        totalSpent: 'إجمالي الإنفاق',
        pendingRequests: 'الطلبات المعلقة',
        completeProfile: 'أكمل ملفك الشخصي',
        tip: 'أكمل جميع الأقسام للحصول على أفضل تجربة'
      }
    : {
        profile: 'Profile',
        subtitle: 'Manage your personal information and settings',
        overview: 'Overview',
        personal: 'Personal Info',
        settings: 'Settings',
        completeness: 'Profile Completeness',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        school: 'School or University',
        grade: 'Grade Level',
        changePassword: 'Change Password',
        oldPassword: 'Old Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        logout: 'Logout',
        deleteAccount: 'Delete Account',
        deleteAccountWarning: 'This will permanently delete your account and all your data',
        deleteConfirmTitle: 'Are you sure?',
        deleteConfirmMessage: 'This action cannot be undone. Your account and all your data will be permanently deleted.',
        cancel: 'Cancel',
        confirmDelete: 'Yes, Delete My Account',
        updateChanges: 'Update Changes',
        totalSessions: 'Total Sessions',
        upcomingSessions: 'Upcoming Sessions',
        completedSessions: 'Completed Sessions',
        totalSpent: 'Total Spent',
        pendingRequests: 'Pending Requests',
        completeProfile: 'Complete Your Profile',
        tip: 'Complete all sections for the best experience'
      };

  const tabs = [
    { 
      id: 'overview', 
      label: t.overview, 
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="14" width="4" height="6" rx="1" fill="#10B981"/>
          <rect x="10" y="10" width="4" height="10" rx="1" fill="#EC4899"/>
          <rect x="16" y="6" width="4" height="14" rx="1" fill="#3B82F6"/>
        </svg>
      )
    },
    { 
      id: 'personal', 
      label: t.personal, 
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="#6B7280"/>
          <path d="M12 14C7.58172 14 4 16.6863 4 20V22H20V20C20 16.6863 16.4183 14 12 14Z" fill="#6B7280"/>
        </svg>
      )
    },
    { 
      id: 'settings', 
      label: t.settings, 
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" fill="#6B7280"/>
          <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" fill="#6B7280"/>
        </svg>
      )
    }
  ];

  return (
    <main
        style={{
        flex: 1,
        paddingTop: isMobile ? '16px' : '40px',
        paddingBottom: isMobile ? '16px' : '40px',
        paddingLeft: isMobile ? '16px' : (sidebarVisible ? '72px' : '48px'),
        paddingRight: isMobile ? '16px' : (sidebarVisible ? '48px' : '48px'),
        minHeight: '100vh',
        background: '#f7f9fb',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif",
        transition: 'padding-left 0.3s, padding-right 0.3s',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden'
        }}
        dir={isArabic ? 'rtl' : 'ltr'}
      >
          <PageHeader
            title={t.profile}
            subtitle={t.subtitle}
            isArabic={isArabic}
          />

      {/* Alert Messages */}
      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '14px 20px',
          borderRadius: 12,
          marginBottom: 20,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: '1px solid #fca5a5'
        }}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div style={{
          background: '#dcfce7',
          color: '#166534',
          padding: '14px 20px',
          borderRadius: 12,
          marginBottom: 20,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: '1px solid #86efac'
        }}>
          <span>✅</span>
          <span>{success}</span>
        </div>
      )}

       {/* Profile Card */}
       <div style={{
             background: '#fff',
             borderRadius: isMobile ? 16 : 24,
         boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
         overflow: 'hidden',
         marginBottom: isMobile ? 16 : 24,
         width: '100%',
         maxWidth: '100%'
       }}>
         {/* Profile Header */}
         <div style={{
           background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
           padding: isMobile ? '20px 16px' : '40px 32px',
           position: 'relative'
         }}>
           <div style={{
             display: 'flex',
             alignItems: isMobile ? 'center' : 'center',
             gap: isMobile ? 12 : 32,
             flexWrap: 'wrap',
             justifyContent: 'center',
             maxWidth: 1200,
             margin: '0 auto',
             flexDirection: isMobile ? 'column' : (isArabic ? 'row-reverse' : 'row'),
             width: '100%'
           }}>
            {/* Profile Photo */}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: isMobile ? 110 : 160,
              height: isMobile ? 110 : 160,
              margin: '0 auto'
            }}>
               <img
                 src={imagePreview || (profile.image && profile.image.trim() !== '' ? profile.image : 'https://www.gravatar.com/avatar/?d=mp&f=y')}
               alt="Profile"
               style={{
                   width: isMobile ? 100 : 150,
                   height: isMobile ? 100 : 150,
                 borderRadius: '50%',
                 objectFit: 'cover',
                   border: isMobile ? '3px solid #fff' : '4px solid #fff',
                   boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
               }}
             />
              {/* Upload Button - Bottom Right */}
            <label
              htmlFor="profile-upload"
              style={{
                position: 'absolute',
                  bottom: isMobile ? 0 : 2,
                  right: isMobile ? 0 : 2,
                  background: '#FF7426',
                borderRadius: '50%',
                  border: isMobile ? '3px solid #fff' : '3px solid #fff',
                  padding: 0,
                cursor: 'pointer',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                  width: isMobile ? 36 : 44,
                  height: isMobile ? 36 : 44,
                  transition: 'all 0.2s ease',
                  zIndex: 10,
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.4)';
                  }
                }}
                onTouchStart={(e) => {
                  e.currentTarget.style.transform = 'scale(0.95)';
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <FaUpload style={{ color: '#fff', fontSize: isMobile ? 16 : 18 }} />
              <input
                id="profile-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
                </label>
               {/* Remove Button - Top Left */}
               {(imagePreview || profile.image) && (
               <button
                 type="button"
                 title={isMobile ? (isArabic ? 'حذف الصورة' : 'Remove picture') : (isArabic ? 'حذف صورة الملف الشخصي' : 'Remove profile picture')}
                 onClick={handleDeleteProfilePicture}
                 disabled={loading}
                style={{
                  position: 'absolute',
                    top: isMobile ? 0 : 2,
                    left: isMobile ? 0 : 2,
                    background: '#dc2626',
                    border: isMobile ? '3px solid #fff' : '3px solid #fff',
                  borderRadius: '50%',
                    width: isMobile ? 36 : 40,
                    height: isMobile ? 36 : 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                    color: '#fff',
                  fontWeight: 900,
                    fontSize: isMobile ? 18 : 22,
                  cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
                    transition: 'all 0.2s ease',
                    zIndex: 10,
                    padding: 0,
                    lineHeight: 1,
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    opacity: loading ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile && !loading) {
                      e.currentTarget.style.transform = 'scale(1.1)';
                      e.currentTarget.style.background = '#b91c1c';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.background = '#dc2626';
                      e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.4)';
                    }
                  }}
                  onTouchStart={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'scale(0.95)';
                    }
                  }}
                  onTouchEnd={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
              >
                ×
                  </button>
                )}
              </div>

             {/* Profile Info */}
             <div style={{ 
               flex: 1, 
               color: '#fff', 
               textAlign: isMobile ? 'center' : (isArabic ? 'right' : 'left'),
               width: isMobile ? '100%' : 'auto',
               minWidth: 0
             }}>
               <div style={{ 
                 fontSize: isMobile ? 22 : 32, 
                 fontWeight: 800, 
                 marginBottom: isMobile ? 6 : 8,
                 wordBreak: 'break-word',
                 overflowWrap: 'break-word'
               }}>
                 {profile.name && profile.name !== 'Not provided' && profile.name !== 'غير متوفر' ? profile.name : 'Student'}
               </div>
               <div style={{ 
                 fontSize: isMobile ? 13 : 16, 
                 opacity: 0.9, 
                 marginBottom: isMobile ? 6 : 4, 
                 wordBreak: 'break-word',
                 overflowWrap: 'break-word',
                 padding: isMobile ? '0 8px' : '0'
               }}>
                 {profile.email}
               </div>
               {profile.school && (
                 <div style={{ 
                   fontSize: isMobile ? 12 : 14, 
                   opacity: 0.8, 
                   display: 'flex', 
                   alignItems: 'center', 
                   gap: 6, 
                   justifyContent: isMobile ? 'center' : (isArabic ? 'flex-end' : 'flex-start'),
                   flexWrap: 'wrap',
                   flexDirection: isArabic ? 'row-reverse' : 'row'
                 }}>
                   <span>🏫</span>
                   <span style={{ wordBreak: 'break-word' }}>{profile.school}</span>
                 </div>
               )}
             </div>

             {/* Profile Completeness Badge */}
             <div style={{
               background: 'rgba(255,255,255,0.2)',
               backdropFilter: 'blur(10px)',
               borderRadius: 16,
               padding: isMobile ? '14px 18px' : '20px 24px',
               textAlign: 'center',
               minWidth: isMobile ? 90 : 140,
               width: isMobile ? '100%' : 'auto',
               maxWidth: isMobile ? '180px' : 'none',
               margin: isMobile ? '0 auto' : '0'
             }}>
               <div style={{ fontSize: isMobile ? 26 : 36, fontWeight: 900, marginBottom: 4 }}>
                 {completeness}%
               </div>
               <div style={{ fontSize: isMobile ? 10 : 12, opacity: 0.9 }}>
                 {t.completeness}
               </div>
             </div>
          </div>
        </div>

         {/* Tabs */}
         <div style={{
           display: 'flex',
           gap: 0,
           padding: isMobile ? '0 8px' : '0 32px',
           borderBottom: '1px solid #e5e7eb',
           background: '#fff',
           overflowX: isMobile ? 'auto' : 'visible',
           overflowY: 'hidden',
           WebkitOverflowScrolling: 'touch',
           justifyContent: isMobile ? 'space-between' : 'flex-start',
           position: 'relative',
           width: '100%'
         }}>
           {tabs.map(tab => (
             <button
               key={tab.id}
               onClick={() => setActiveTab(tab.id)}
               style={{
                 flex: isMobile ? '1 1 0' : 'none',
                 padding: isMobile ? '12px 8px' : '16px 24px',
                 borderRadius: activeTab === tab.id ? '12px 12px 0 0' : '0',
                 fontWeight: 600,
                 fontSize: isMobile ? 12 : 15,
                 cursor: 'pointer',
                 transition: 'all 0.2s ease',
                 border: 'none',
                 background: activeTab === tab.id ? '#FF7426' : 'transparent',
                 color: activeTab === tab.id ? '#fff' : '#6b7280',
                 display: 'flex',
                 flexDirection: 'row',
                 alignItems: 'center',
                 justifyContent: 'center',
                 gap: isMobile ? 6 : 8,
                 marginBottom: 0,
                 whiteSpace: 'nowrap',
                 minWidth: isMobile ? 'min-content' : 'auto',
                 position: 'relative',
                 zIndex: activeTab === tab.id ? 1 : 0,
                 touchAction: 'manipulation',
                 WebkitTapHighlightColor: 'transparent'
               }}
              onMouseEnter={e => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.color = '#374151';
                }
              }}
              onMouseLeave={e => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }
              }}
            >
              <span style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 18,
                height: 18,
                flexShrink: 0
              }}>
                {activeTab === tab.id ? (
                  // Active tab icons - white
                  tab.id === 'overview' ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="4" y="14" width="4" height="6" rx="1" fill="#fff"/>
                      <rect x="10" y="10" width="4" height="10" rx="1" fill="#fff"/>
                      <rect x="16" y="6" width="4" height="14" rx="1" fill="#fff"/>
                    </svg>
                  ) : tab.id === 'personal' ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="#fff"/>
                      <path d="M12 14C7.58172 14 4 16.6863 4 20V22H20V20C20 16.6863 16.4183 14 12 14Z" fill="#fff"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" fill="#fff"/>
                      <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" fill="#fff"/>
                    </svg>
                  )
                ) : tab.icon}
              </span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ 
          padding: isMobile ? '16px 12px' : '32px',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div style={{ width: '100%', maxWidth: '100%' }}>
              {/* Statistics Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: isMobile ? 12 : 20,
                marginBottom: isMobile ? 20 : 32,
                width: '100%'
              }}>
                {/* Total Sessions Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 16 : 24,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? 10 : 12,
                  width: '100%',
                  minWidth: 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 }}>
                    <div style={{
                      width: isMobile ? 40 : 48,
                      height: isMobile ? 40 : 48,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width={isMobile ? "20" : "24"} height={isMobile ? "20" : "24"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L3 7L12 12L21 7L12 2Z" fill="#3b82f6"/>
                        <path d="M3 17L12 22L21 17M3 12L12 17L21 12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div style={{ 
                      fontSize: isMobile ? 10 : 11, 
                      fontWeight: 600, 
                      color: '#6b7280', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {t.totalSessions}
                    </div>
                  </div>
                  <div style={{ fontSize: isMobile ? 28 : 32, fontWeight: 700, color: '#111827', lineHeight: 1 }}>
                    {loadingStats ? '...' : stats.totalSessions}
                  </div>
                </div>

                {/* Upcoming Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 16 : 24,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? 10 : 12,
                  width: '100%',
                  minWidth: 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 }}>
                    <div style={{
                      width: isMobile ? 40 : 48,
                      height: isMobile ? 40 : 48,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width={isMobile ? "20" : "24"} height={isMobile ? "20" : "24"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="6" width="14" height="14" rx="2.5" fill="#F58822"/>
                        <path d="M5 10H19" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M9 6V4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M15 6V4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="9" cy="14" r="1.2" fill="white"/>
                        <circle cx="12" cy="14" r="1.2" fill="white"/>
                        <circle cx="15" cy="14" r="1.2" fill="white"/>
                        <circle cx="9" cy="17.5" r="1.2" fill="white"/>
                        <circle cx="12" cy="17.5" r="1.2" fill="white"/>
                      </svg>
                    </div>
                    <div style={{ 
                      fontSize: isMobile ? 10 : 11, 
                      fontWeight: 600, 
                      color: '#6b7280', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {t.upcomingSessions}
                    </div>
                  </div>
                  <div style={{ fontSize: isMobile ? 28 : 32, fontWeight: 700, color: '#111827', lineHeight: 1 }}>
                    {loadingStats ? '...' : stats.upcomingSessions}
                  </div>
                </div>

                {/* Completed Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 16 : 24,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? 10 : 12,
                  width: '100%',
                  minWidth: 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 }}>
                    <div style={{
                      width: isMobile ? 40 : 48,
                      height: isMobile ? 40 : 48,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #a7f3d0 0%, #6ee7b7 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width={isMobile ? "20" : "24"} height={isMobile ? "20" : "24"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="#10b981"/>
                        <path d="M8 12L10.5 14.5L16 9" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div style={{ 
                      fontSize: isMobile ? 10 : 11, 
                      fontWeight: 600, 
                      color: '#6b7280', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {t.completedSessions}
                    </div>
                  </div>
                  <div style={{ fontSize: isMobile ? 28 : 32, fontWeight: 700, color: '#111827', lineHeight: 1 }}>
                    {loadingStats ? '...' : stats.completedSessions}
                  </div>
                </div>

                {/* Total Spent Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 16 : 24,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? 10 : 12,
                  width: '100%',
                  minWidth: 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 }}>
                    <div style={{
                      width: isMobile ? 40 : 48,
                      height: isMobile ? 40 : 48,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #e9d5ff 0%, #d8b4fe 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width={isMobile ? "20" : "24"} height={isMobile ? "20" : "24"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="9" fill="#8b5cf6"/>
                        <path d="M12 7V17M7 12H17" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                        <circle cx="12" cy="12" r="2.5" fill="white" opacity="0.4"/>
                      </svg>
                    </div>
                    <div style={{ 
                      fontSize: isMobile ? 10 : 11, 
                      fontWeight: 600, 
                      color: '#6b7280', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.5px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {t.totalSpent}
                    </div>
                  </div>
                  <div style={{ fontSize: isMobile ? 28 : 32, fontWeight: 700, color: '#111827', lineHeight: 1 }}>
                    {loadingStats ? '...' : `${stats.totalSpent.toFixed(1)} BD`}
                  </div>
                </div>
              </div>

              {/* Main Content Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? 16 : 32,
                width: '100%'
              }}>
                {/* Left Column */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: isMobile ? 16 : 32,
                  width: '100%',
                  minWidth: 0
                }}>
                  {/* Personal Information */}
                  <div style={{
                    background: '#fff',
                    borderRadius: 12,
                    padding: isMobile ? 16 : 24,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    width: '100%'
                  }}>
                    <div style={{ 
                      fontSize: isMobile ? 16 : 18, 
                      fontWeight: 700, 
                      color: '#111827', 
                      marginBottom: isMobile ? 16 : 20 
                    }}>
                      {isArabic ? 'المعلومات الشخصية' : 'Personal Information'}
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: isMobile ? 14 : 24
                    }}>
                      {/* Left Column */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* Email */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                            {isArabic ? 'البريد الإلكتروني' : 'Email Address'}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>
                              {profile.email || 'Not provided'}
                            </span>
                            {profile.email && (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L4 5V11C4 16.55 7.16 21.74 12 23C16.84 21.74 20 16.55 20 11V5L12 2Z" fill="#10b981"/>
                                <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* Phone Number */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                            {isArabic ? 'رقم الهاتف' : 'Phone Number'}
                          </div>
                          <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>
                            {profile.phone || (isArabic ? 'غير متوفر' : 'Not provided')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Profile Completeness */}
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 16 : 24,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  height: 'fit-content',
                  width: '100%'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    marginBottom: isMobile ? 16 : 20,
                    flexWrap: 'wrap',
                    gap: isMobile ? 8 : 0
                  }}>
                    <div style={{ 
                      fontSize: isMobile ? 16 : 18, 
                      fontWeight: 700, 
                      color: '#111827' 
                    }}>
                      {isArabic ? 'اكتمال الملف الشخصي' : 'Profile Completeness'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#10b981', fontSize: isMobile ? 16 : 18 }}>✓</span>
                      <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#10b981' }}>
                        {completeness}%
                      </span>
                    </div>
                  </div>
                  <div style={{
                    width: '100%',
                    height: isMobile ? 6 : 8,
                    background: '#e5e7eb',
                    borderRadius: 999,
                    overflow: 'hidden',
                    marginBottom: isMobile ? 16 : 20
                  }}>
                    <div style={{
                      width: `${completeness}%`,
                      height: '100%',
                      background: '#10b981',
                      borderRadius: 999,
                      transition: 'width 0.5s ease'
                    }}></div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      { key: 'name', label: isArabic ? 'الاسم' : 'Name', complete: (() => {
                          const val = profile.name;
                          return val && val.trim() !== '' && val !== 'Not provided' && val !== 'غير متوفر';
                        })() },
                      { key: 'email', label: isArabic ? 'البريد الإلكتروني' : 'Email', complete: (() => {
                          const val = profile.email;
                          return val && val.trim() !== '' && val !== 'Not provided' && val !== 'غير متوفر' && val.includes('@');
                        })() },
                      { key: 'phone', label: isArabic ? 'الهاتف' : 'Phone', complete: (() => {
                          const val = profile.phone;
                          return val && val.trim() !== '' && val !== 'Not provided' && val !== 'غير متوفر';
                        })() },
                      { key: 'school', label: isArabic ? 'المدرسة أو الجامعة' : 'School or University', complete: (() => {
                          const val = profile.school;
                          return val && val.trim() !== '' && val !== 'Other';
                        })() },
                      { key: 'grade', label: isArabic ? 'المستوى الدراسي' : 'Grade Level', complete: (() => {
                          const val = profile.grade;
                          return val && val.trim() !== '';
                        })() },
                      { key: 'photo', label: isArabic ? 'الصورة' : 'Photo', complete: !!(imagePreview || profile.image) }
                    ]
                    .sort((a, b) => {
                      // Sort: completed items first, then incomplete
                      if (a.complete && !b.complete) return -1;
                      if (!a.complete && b.complete) return 1;
                      return 0;
                    })
                    .map(item => (
                      <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ color: '#10b981', fontSize: 16 }}>
                          {item.complete ? '✓' : ''}
                        </span>
                        <span style={{
                          fontSize: 14,
                          color: item.complete ? '#111827' : '#9ca3af',
                          fontWeight: item.complete ? 500 : 400
                        }}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Personal Info Tab */}
          {activeTab === 'personal' && (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <div style={{ maxWidth: 600, margin: '0 auto' }}>
                {/* Header with Edit Button */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: 24
                }}>
                  <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>
                    {isArabic ? 'المعلومات الشخصية' : 'Personal Info'}
                  </h2>
                  {!isEditingPersonalInfo ? (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingPersonalInfo(true);
                        // Initialize all edited values
                        setEditedName(profile.name === 'Not provided' || profile.name === 'غير متوفر' ? '' : profile.name);
                        setEditedPhone(profile.phone === 'Not provided' || profile.phone === 'غير متوفر' ? '' : profile.phone);
                      }}
                       style={{
                         padding: isMobile ? '10px 16px' : '10px 20px',
                         background: '#FF7426',
                         color: '#fff',
                         border: 'none',
                         borderRadius: 10,
                         fontWeight: 700,
                         fontSize: isMobile ? 13 : 14,
                         cursor: 'pointer',
                         transition: 'all 0.2s',
                         boxShadow: '0 2px 8px rgba(255,116,38,0.2)',
                         touchAction: 'manipulation',
                         minHeight: isMobile ? '44px' : 'auto'
                       }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = '#FF8C42';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(255,116,38,0.3)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = '#FF7426';
                        e.currentTarget.style.transform = 'none';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(255,116,38,0.2)';
                      }}
                    >
                      {isArabic ? 'تعديل' : 'Edit'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingPersonalInfo(false);
                        setEditedName(profile.name === 'Not provided' || profile.name === 'غير متوفر' ? '' : profile.name);
                        setEditedPhone(profile.phone === 'Not provided' || profile.phone === 'غير متوفر' ? '' : profile.phone);
                      }}
                      style={{
                        padding: '10px 20px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = '#e5e7eb';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = '#f3f4f6';
                      }}
                    >
                      {isArabic ? 'إلغاء' : 'Cancel'}
                    </button>
                  )}
                </div>

                 {/* Name */}
                 <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                   <label style={{ 
                     fontWeight: 700, 
                     fontSize: isMobile ? 14 : 15, 
                     marginBottom: isMobile ? 6 : 8, 
                     display: 'block', 
                     color: '#374151' 
                   }}>
                     {t.name}
                   </label>
                   {isEditingPersonalInfo ? (
                     <input
                       type="text"
                       name="name"
                       value={editedName}
                       onChange={(e) => setEditedName(e.target.value)}
                       placeholder={isArabic ? 'أدخل الاسم' : 'Enter name'}
                       style={{
                         width: '100%',
                         padding: isMobile ? 12 : 14,
                         borderRadius: 12,
                         border: '2px solid #e5e7eb',
                         fontSize: isMobile ? 15 : 16,
                         background: '#fff',
                         color: '#111827',
                         fontWeight: 500,
                         outline: 'none',
                         transition: 'all 0.2s',
                         boxSizing: 'border-box',
                         WebkitAppearance: 'none'
                       }}
                      onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  ) : (
                    <input
                      type="text"
                      name="name"
                      value={profile.name && profile.name !== 'Not provided' && profile.name !== 'غير متوفر' ? profile.name : (isArabic ? 'غير متوفر' : 'Not provided')}
                      disabled
                      style={{
                        width: '100%',
                        padding: 14,
                        borderRadius: 12,
                        border: '2px solid #e5e7eb',
                        fontSize: 16,
                        background: '#f9fafb',
                        color: '#6b7280',
                        fontWeight: 600
                      }}
                    />
                  )}
                </div>

                 {/* Email */}
                 <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                   <label style={{ 
                     fontWeight: 700, 
                     fontSize: isMobile ? 14 : 15, 
                     marginBottom: isMobile ? 6 : 8, 
                     display: 'block', 
                     color: '#374151' 
                   }}>
                     {t.email}
                   </label>
                   <input
                     type="email"
                     name="email"
                     value={profile.email}
                     disabled
                     style={{
                       width: '100%',
                       padding: isMobile ? 12 : 14,
                       borderRadius: 12,
                       border: '2px solid #e5e7eb',
                       fontSize: isMobile ? 15 : 16,
                       background: '#f9fafb',
                       color: '#6b7280',
                       fontWeight: 600,
                       boxSizing: 'border-box',
                       WebkitAppearance: 'none'
                     }}
                   />
                 </div>

                 {/* Phone */}
                 <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                   <label style={{ 
                     fontWeight: 700, 
                     fontSize: isMobile ? 14 : 15, 
                     marginBottom: isMobile ? 6 : 8, 
                     display: 'block', 
                     color: '#374151' 
                   }}>
                     {t.phone}
                   </label>
                   {isEditingPersonalInfo ? (
                     <input
                       type="tel"
                       name="phone"
                       value={editedPhone}
                       onChange={(e) => setEditedPhone(e.target.value)}
                       placeholder={isArabic ? 'أدخل رقم الهاتف' : 'Enter phone number'}
                       style={{
                         width: '100%',
                         padding: isMobile ? 12 : 14,
                         borderRadius: 12,
                         border: '2px solid #e5e7eb',
                         fontSize: isMobile ? 15 : 16,
                         background: '#fff',
                         color: '#111827',
                         fontWeight: 500,
                         outline: 'none',
                         transition: 'all 0.2s',
                         boxSizing: 'border-box',
                         WebkitAppearance: 'none'
                       }}
                      onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  ) : (
                    <input
                      type="text"
                      name="phone"
                      value={profile.phone && profile.phone !== 'Not provided' && profile.phone !== 'غير متوفر' ? profile.phone : (isArabic ? 'غير متوفر' : 'Not provided')}
                      disabled
                      style={{
                        width: '100%',
                        padding: 14,
                        borderRadius: 12,
                        border: '2px solid #e5e7eb',
                        fontSize: 16,
                        background: '#f9fafb',
                        color: '#6b7280',
                        fontWeight: 600
                      }}
                    />
                  )}
                </div>

                 {/* Institution Type */}
                 <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                   <label style={{ 
                     fontWeight: 700, 
                     fontSize: isMobile ? 14 : 15, 
                     marginBottom: isMobile ? 6 : 8, 
                     display: 'block', 
                     color: '#374151' 
                   }}>
                     {isArabic ? 'نوع المؤسسة التعليمية' : 'Institution Type'}
                   </label>
                   <select
                     name="institutionType"
                     value={institutionType}
                     onChange={handleInstitutionTypeChange}
                     disabled={!isEditingPersonalInfo}
                     style={{
                       width: '100%',
                       padding: isMobile ? 12 : 14,
                       borderRadius: 12,
                       border: '2px solid #e5e7eb',
                       fontSize: isMobile ? 15 : 16,
                       background: isEditingPersonalInfo ? '#fff' : '#f9fafb',
                       color: isEditingPersonalInfo ? '#111827' : '#6b7280',
                       cursor: isEditingPersonalInfo ? 'pointer' : 'not-allowed',
                       outline: 'none',
                       transition: 'all 0.2s',
                       boxSizing: 'border-box',
                       WebkitAppearance: 'none',
                       minHeight: isMobile ? '44px' : 'auto'
                     }}
                    onFocus={(e) => isEditingPersonalInfo && (e.target.style.borderColor = '#FF7426')}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  >
                    <option value="">{isArabic ? 'اختر نوع المؤسسة' : 'Select Institution Type'}</option>
                    <option value="school">{isArabic ? 'مدرسة' : 'School'}</option>
                    <option value="university">{isArabic ? 'جامعة' : 'University'}</option>
                  </select>
                </div>

                {/* School / University */}
                {institutionType && (
                  <>
                    <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                      <label style={{ 
                        fontWeight: 700, 
                        fontSize: isMobile ? 14 : 15, 
                        marginBottom: isMobile ? 6 : 8, 
                        display: 'block', 
                        color: '#374151' 
                      }}>
                        {institutionType === 'school'
                          ? (isArabic ? 'المدرسة' : 'School')
                          : (isArabic ? 'الجامعة' : 'University')
                        }
                      </label>
                      <select
                        name="school"
                        value={profile.school}
                        onChange={handleChange}
                        disabled={!isEditingPersonalInfo}
                        style={{
                          width: '100%',
                          padding: isMobile ? 12 : 14,
                          borderRadius: 12,
                          border: '2px solid #e5e7eb',
                          fontSize: isMobile ? 15 : 16,
                          background: isEditingPersonalInfo ? '#fff' : '#f9fafb',
                          color: isEditingPersonalInfo ? '#111827' : '#6b7280',
                          cursor: isEditingPersonalInfo ? 'pointer' : 'not-allowed',
                          outline: 'none',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box',
                          WebkitAppearance: 'none',
                          minHeight: isMobile ? '44px' : 'auto'
                        }}
                        onFocus={(e) => isEditingPersonalInfo && (e.target.style.borderColor = '#FF7426')}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      >
                        <option value="">
                          {institutionType === 'school'
                            ? (isArabic ? 'اختر المدرسة' : 'Select School')
                            : (isArabic ? 'اختر الجامعة' : 'Select University')
                          }
                        </option>
                        {(institutionType === 'school' ? bahrainSchools : bahrainUniversities).map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Custom Institution Input - Shows when "Other" is selected */}
                    {profile.school === 'Other' && (
                      <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                        <label style={{ 
                          fontWeight: 700, 
                          fontSize: isMobile ? 14 : 15, 
                          marginBottom: isMobile ? 6 : 8, 
                          display: 'block', 
                          color: '#374151' 
                        }}>
                          {institutionType === 'school'
                            ? (isArabic ? 'أدخل اسم المدرسة' : 'Enter School Name')
                            : (isArabic ? 'أدخل اسم الجامعة' : 'Enter University Name')
                          }
                        </label>
                        <input
                          type="text"
                          name="customInstitution"
                          value={customInstitution}
                          onChange={(e) => setCustomInstitution(e.target.value)}
                          disabled={!isEditingPersonalInfo}
                          placeholder={
                            institutionType === 'school'
                              ? (isArabic ? 'اكتب اسم المدرسة' : 'Type your school name')
                              : (isArabic ? 'اكتب اسم الجامعة' : 'Type your university name')
                          }
                          style={{
                            width: '100%',
                            padding: isMobile ? 12 : 14,
                            borderRadius: 12,
                            border: '2px solid #e5e7eb',
                            fontSize: isMobile ? 15 : 16,
                            background: isEditingPersonalInfo ? '#fff' : '#f9fafb',
                            color: isEditingPersonalInfo ? '#111827' : '#6b7280',
                            cursor: isEditingPersonalInfo ? 'text' : 'not-allowed',
                            outline: 'none',
                            transition: 'all 0.2s',
                            boxSizing: 'border-box',
                            WebkitAppearance: 'none'
                          }}
                          onFocus={(e) => isEditingPersonalInfo && (e.target.style.borderColor = '#FF7426')}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Grade */}
                {institutionType && (
                  <div style={{ marginBottom: isMobile ? 24 : 32 }}>
                    <label style={{ 
                      fontWeight: 700, 
                      fontSize: isMobile ? 14 : 15, 
                      marginBottom: isMobile ? 6 : 8, 
                      display: 'block', 
                      color: '#374151' 
                    }}>
                      {t.grade}
                    </label>
                    <select
                      name="grade"
                      value={profile.grade}
                      onChange={handleChange}
                      disabled={!isEditingPersonalInfo}
                      style={{
                        width: '100%',
                        padding: isMobile ? 12 : 14,
                        borderRadius: 12,
                        border: '2px solid #e5e7eb',
                        fontSize: isMobile ? 15 : 16,
                        background: isEditingPersonalInfo ? '#fff' : '#f9fafb',
                        color: isEditingPersonalInfo ? '#111827' : '#6b7280',
                        cursor: isEditingPersonalInfo ? 'pointer' : 'not-allowed',
                        outline: 'none',
                        transition: 'all 0.2s',
                        boxSizing: 'border-box',
                        WebkitAppearance: 'none',
                        minHeight: isMobile ? '44px' : 'auto'
                      }}
                      onFocus={(e) => isEditingPersonalInfo && (e.target.style.borderColor = '#FF7426')}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    >
                      <option value="">{isArabic ? 'اختر المستوى الدراسي' : 'Select Grade Level'}</option>
                      {(institutionType === 'university' ? universityGrades : schoolGrades).map((grade) => (
                        <option key={grade} value={grade}>{grade}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Preferred Location Section */}
                <div style={{ marginBottom: isMobile ? 24 : 32 }}>
                  <label style={{
                    fontWeight: 700,
                    fontSize: isMobile ? 14 : 15,
                    marginBottom: isMobile ? 6 : 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: '#374151'
                  }}>
                    <span>
                      {isArabic ? 'الموقع المفضل (اختياري)' : 'Preferred Location (Optional)'}
                    </span>
                    {profile.preferred_location_formatted_address && isEditingPersonalInfo && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(isArabic
                            ? 'هل تريد حذف الموقع المفضل؟'
                            : 'Clear preferred location?')) {
                            setProfile({
                              ...profile,
                              preferred_location_name: '',
                              preferred_location_latitude: null,
                              preferred_location_longitude: null,
                              preferred_location_place_id: null,
                              preferred_location_formatted_address: ''
                            });
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        {isArabic ? 'حذف' : 'Clear'}
                      </button>
                    )}
                  </label>

                  {profile.preferred_location_formatted_address ? (
                    <div style={{
                      padding: isMobile ? 12 : 14,
                      borderRadius: 12,
                      border: '2px solid #e5e7eb',
                      background: '#f9fafb'
                    }}>
                      <div style={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: '#111827',
                        marginBottom: 4
                      }}>
                        {profile.preferred_location_name || (isArabic ? 'موقعي' : 'My Location')}
                      </div>
                      <div style={{
                        fontSize: 13,
                        color: '#6b7280',
                        marginBottom: 8
                      }}>
                        {profile.preferred_location_formatted_address}
                      </div>
                      {isEditingPersonalInfo && (
                        <button
                          type="button"
                          onClick={() => setShowLocationPicker(!showLocationPicker)}
                          style={{
                            padding: '8px 14px',
                            background: '#fff',
                            color: '#FF7426',
                            border: '2px solid #FF7426',
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          {showLocationPicker
                            ? (isArabic ? 'إخفاء الخريطة' : 'Hide Map')
                            : (isArabic ? 'تغيير الموقع' : 'Change Location')
                          }
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => isEditingPersonalInfo && setShowLocationPicker(!showLocationPicker)}
                      disabled={!isEditingPersonalInfo}
                      style={{
                        width: '100%',
                        padding: isMobile ? 12 : 14,
                        borderRadius: 12,
                        border: '2px dashed #e5e7eb',
                        background: isEditingPersonalInfo ? '#fff' : '#f9fafb',
                        color: isEditingPersonalInfo ? '#64748b' : '#9ca3af',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: isEditingPersonalInfo ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8
                      }}
                    >
                      {isArabic ? 'إضافة موقع مفضل' : 'Add Preferred Location'}
                    </button>
                  )}

                  {showLocationPicker && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{
                          fontWeight: 600,
                          fontSize: 13,
                          marginBottom: 6,
                          display: 'block',
                          color: '#374151'
                        }}>
                          {isArabic ? 'اسم الموقع (مثل: "منزلي", "مسكني")' : 'Location Name (e.g., "My Home", "My Dorm")'}
                        </label>
                        <input
                          type="text"
                          value={profile.preferred_location_name}
                          onChange={(e) => setProfile({
                            ...profile,
                            preferred_location_name: e.target.value
                          })}
                          disabled={!isEditingPersonalInfo}
                          placeholder={isArabic ? 'أدخل اسم الموقع' : 'Enter location name'}
                          style={{
                            width: '100%',
                            padding: 10,
                            borderRadius: 8,
                            border: '2px solid #e5e7eb',
                            fontSize: 14,
                            background: isEditingPersonalInfo ? '#fff' : '#f9fafb',
                            color: isEditingPersonalInfo ? '#111827' : '#6b7280',
                            cursor: isEditingPersonalInfo ? 'text' : 'not-allowed',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <LocationPicker
                        mode="single"
                        initialLocation={
                          profile.preferred_location_latitude && profile.preferred_location_longitude
                            ? {
                                lat: profile.preferred_location_latitude,
                                lng: profile.preferred_location_longitude
                              }
                            : null
                        }
                        onLocationSelect={(location) => {
                          if (location && isEditingPersonalInfo) {
                            setProfile({
                              ...profile,
                              preferred_location_latitude: location.latitude,
                              preferred_location_longitude: location.longitude,
                              preferred_location_place_id: location.place_id,
                              preferred_location_formatted_address: location.formatted_address
                            });
                          }
                        }}
                        showAutocomplete={isEditingPersonalInfo}
                        showClickToSelect={isEditingPersonalInfo}
                        readOnly={!isEditingPersonalInfo}
                        height="350px"
                        isArabic={isArabic}
                      />

                      <div style={{
                        marginTop: 8,
                        padding: 8,
                        background: '#eff6ff',
                        borderRadius: 8,
                        fontSize: 12,
                        color: '#1e40af'
                      }}>
                        {isArabic
                          ? 'سيتم عرض هذا الموقع كخيار عند حجز الدروس وجهاً لوجه'
                          : 'This location will appear as an option when booking face-to-face lessons'
                        }
                      </div>
                    </div>
                  )}
                </div>

                 {/* Update Button */}
                 <button
                   type="submit"
               style={{
                 width: '100%',
                     padding: isMobile ? '14px' : '16px',
                     background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
                     color: '#fff',
                     border: 'none',
                     borderRadius: 12,
                     fontWeight: 800,
                     fontSize: isMobile ? 15 : 16,
                     cursor: loading ? 'not-allowed' : 'pointer',
                     boxShadow: '0 4px 16px rgba(255,116,38,0.3)',
                     transition: 'all 0.2s',
                     opacity: loading ? 0.7 : 1,
                     touchAction: 'manipulation',
                     minHeight: isMobile ? '48px' : 'auto',
                     WebkitTapHighlightColor: 'transparent'
                   }}
                  disabled={loading || !isEditingPersonalInfo}
                  onMouseEnter={e => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(255,116,38,0.4)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(255,116,38,0.3)';
                    }
                  }}
                >
                  {loading ? (isArabic ? 'جارٍ الحفظ...' : 'Saving...') : t.updateChanges}
                </button>
              </div>
            </form>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div style={{ 
              maxWidth: isMobile ? '100%' : 600, 
              margin: '0 auto',
              width: '100%'
            }}>
              {/* Change Password Section */}
              <div style={{
                background: '#f8fafc',
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? 16 : 24,
                marginBottom: isMobile ? 20 : 24,
                border: '1px solid #e5e7eb',
                width: '100%'
              }}>
                <div style={{ 
                  fontSize: isMobile ? 18 : 20, 
                  fontWeight: 800, 
                  color: '#111827', 
                  marginBottom: isMobile ? 16 : 20 
                }}>
                  {t.changePassword}
                </div>
                <form onSubmit={handleChangePassword}>
                  <div style={{ marginBottom: isMobile ? 14 : 16 }}>
                    <label style={{ 
                      fontWeight: 700, 
                      fontSize: isMobile ? 13 : 14, 
                      marginBottom: isMobile ? 6 : 8, 
                      display: 'block', 
                      color: '#374151' 
                    }}>
                      {t.oldPassword}
                </label>
                    <div style={{ position: 'relative' }}>
                <input
                        type={showOldPassword ? 'text' : 'password'}
                  name="oldPassword"
                  value={profile.oldPassword}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                          padding: isMobile ? '12px 40px 12px 12px' : '14px 45px 14px 14px',
                          borderRadius: 12,
                          border: '2px solid #e5e7eb',
                    fontSize: isMobile ? 15 : 16,
                          background: '#fff',
                    outline: 'none',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box',
                          WebkitAppearance: 'none',
                          minHeight: isMobile ? '44px' : 'auto'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        style={{
                          position: 'absolute',
                          right: isMobile ? 10 : 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          fontSize: isMobile ? 16 : 18,
                          padding: isMobile ? 8 : 4,
                          touchAction: 'manipulation',
                          minWidth: isMobile ? '44px' : 'auto',
                          minHeight: isMobile ? '44px' : 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
              </div>
                  </div>
                  <div style={{ marginBottom: isMobile ? 14 : 16 }}>
                    <label style={{ 
                      fontWeight: 700, 
                      fontSize: isMobile ? 13 : 14, 
                      marginBottom: isMobile ? 6 : 8, 
                      display: 'block', 
                      color: '#374151' 
                    }}>
                      {t.newPassword}
                </label>
                    <div style={{ position: 'relative' }}>
                <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={profile.newPassword}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                          padding: isMobile ? '12px 40px 12px 12px' : '14px 45px 14px 14px',
                          borderRadius: 12,
                          border: '2px solid #e5e7eb',
                    fontSize: isMobile ? 15 : 16,
                          background: '#fff',
                    outline: 'none',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box',
                          WebkitAppearance: 'none',
                          minHeight: isMobile ? '44px' : 'auto'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
              style={{
                          position: 'absolute',
                          right: isMobile ? 10 : 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          fontSize: isMobile ? 16 : 18,
                          padding: isMobile ? 8 : 4,
                          touchAction: 'manipulation',
                          minWidth: isMobile ? '44px' : 'auto',
                          minHeight: isMobile ? '44px' : 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                    <label style={{ 
                      fontWeight: 700, 
                      fontSize: isMobile ? 13 : 14, 
                      marginBottom: isMobile ? 6 : 8, 
                      display: 'block', 
                      color: '#374151' 
                    }}>
                      {t.confirmPassword}
                </label>
                    <div style={{ position: 'relative' }}>
                <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={profile.confirmPassword}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                          padding: isMobile ? '12px 40px 12px 12px' : '14px 45px 14px 14px',
                          borderRadius: 12,
                          border: '2px solid #e5e7eb',
                    fontSize: isMobile ? 15 : 16,
                          background: '#fff',
                    outline: 'none',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box',
                          WebkitAppearance: 'none',
                          minHeight: isMobile ? '44px' : 'auto'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                          position: 'absolute',
                          right: isMobile ? 10 : 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          fontSize: isMobile ? 16 : 18,
                          padding: isMobile ? 8 : 4,
                          touchAction: 'manipulation',
                          minWidth: isMobile ? '44px' : 'auto',
                          minHeight: isMobile ? '44px' : 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
              <button
                    type="submit"
                style={{
                      width: '100%',
                      padding: isMobile ? '12px' : '14px',
                  background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                      fontWeight: 700,
                  fontSize: isMobile ? 15 : 16,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 16px rgba(255,116,38,0.3)',
                      transition: 'all 0.2s',
                      opacity: loading ? 0.7 : 1,
                      touchAction: 'manipulation',
                      minHeight: isMobile ? '48px' : 'auto',
                      WebkitTapHighlightColor: 'transparent'
                }}
                disabled={loading}
              >
                    {loading ? (isArabic ? 'جارٍ التحديث...' : 'Updating...') : t.changePassword}
                  </button>
                </form>
              </div>

              {/* Logout Section */}
              <div style={{
                background: '#fef2f2',
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? 16 : 24,
                border: '1px solid #fecaca',
                width: '100%'
              }}>
                <div style={{
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 800,
                  color: '#991b1b',
                  marginBottom: isMobile ? 8 : 12
                }}>
                  {isArabic ? 'إدارة الحساب' : 'Account Management'}
                </div>
                <div style={{
                  fontSize: isMobile ? 12 : 14,
                  color: '#7f1d1d',
                  marginBottom: isMobile ? 14 : 20,
                  lineHeight: 1.5
                }}>
                  {isArabic
                    ? 'يمكنك تسجيل الخروج من حسابك في أي وقت'
                    : 'You can sign out of your account at any time'}
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 10 : 12,
                  width: '100%'
                }}>
                  <button
                    type="button"
                    onClick={handleLogout}
                    style={{
                      width: isMobile ? '100%' : 'auto',
                      padding: isMobile ? '12px 20px' : '12px 24px',
                      background: '#dc2626',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: isMobile ? 14 : 15,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      touchAction: 'manipulation',
                      minHeight: isMobile ? '48px' : 'auto',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = '#b91c1c';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = '#dc2626';
                      e.currentTarget.style.transform = 'none';
                    }}
                  >
                    {t.logout}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    style={{
                      width: isMobile ? '100%' : 'auto',
                      padding: isMobile ? '12px 20px' : '12px 24px',
                      background: '#7f1d1d',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: isMobile ? 14 : 15,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      touchAction: 'manipulation',
                      minHeight: isMobile ? '48px' : 'auto',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = '#991b1b';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = '#7f1d1d';
                      e.currentTarget.style.transform = 'none';
                    }}
                  >
                    {t.deleteAccount}
                  </button>
                </div>
                {/* Delete Account Warning */}
                <div style={{
                  marginTop: isMobile ? 12 : 16,
                  padding: isMobile ? 12 : 12,
                  background: '#fee2e2',
                  borderRadius: 10,
                  border: '1px solid #fca5a5',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: isMobile ? 10 : 8
                }}>
                  <span style={{ fontSize: isMobile ? 16 : 16, flexShrink: 0 }}>⚠️</span>
                  <span style={{
                    fontSize: isMobile ? 12 : 13,
                    color: '#7f1d1d',
                    lineHeight: 1.6
                  }}>
                    {t.deleteAccountWarning}
                  </span>
                </div>
              </div>

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999,
                  backdropFilter: 'blur(4px)',
                  padding: isMobile ? '16px' : '0'
                }}>
                  <div style={{
                    background: '#fff',
                    borderRadius: isMobile ? 16 : 20,
                    padding: isMobile ? 20 : 32,
                    maxWidth: isMobile ? '100%' : 480,
                    width: isMobile ? '100%' : '90%',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                    animation: 'modalFadeIn 0.2s ease-out',
                    maxHeight: isMobile ? '90vh' : 'none',
                    overflowY: isMobile ? 'auto' : 'visible'
                  }}>
                    <div style={{ 
                      fontSize: isMobile ? 20 : 24, 
                      fontWeight: 900, 
                      color: '#dc2626', 
                      marginBottom: isMobile ? 12 : 16, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: isMobile ? 8 : 12,
                      flexWrap: 'wrap'
                    }}>
                      <span style={{ fontSize: isMobile ? 24 : 32 }}>⚠️</span>
                      <span>{t.deleteConfirmTitle}</span>
                    </div>
                    <div style={{ 
                      fontSize: isMobile ? 14 : 15, 
                      color: '#374151', 
                      marginBottom: isMobile ? 20 : 28, 
                      lineHeight: 1.6 
                    }}>
                      {t.deleteConfirmMessage}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      gap: isMobile ? 10 : 12, 
                      justifyContent: 'flex-end',
                      flexDirection: isMobile ? 'column-reverse' : 'row',
                      width: '100%'
                    }}>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={loading}
                        style={{
                          padding: isMobile ? '12px 20px' : '14px 24px',
                          background: '#f3f4f6',
                          color: '#374151',
                          border: 'none',
                          borderRadius: 12,
                          fontWeight: 700,
                          fontSize: isMobile ? 14 : 15,
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          opacity: loading ? 0.5 : 1,
                          width: isMobile ? '100%' : 'auto',
                          touchAction: 'manipulation',
                          minHeight: isMobile ? '48px' : 'auto',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                        onMouseEnter={e => {
                          if (!loading) {
                            e.currentTarget.style.background = '#e5e7eb';
                          }
                        }}
                        onMouseOut={e => {
                          if (!loading) {
                            e.currentTarget.style.background = '#f3f4f6';
                          }
                        }}
                      >
                        {t.cancel}
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteAccount}
                        disabled={loading}
                        style={{
                          padding: isMobile ? '12px 20px' : '14px 24px',
                          background: '#dc2626',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 12,
                          fontWeight: 700,
                          fontSize: isMobile ? 14 : 15,
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          opacity: loading ? 0.7 : 1,
                          width: isMobile ? '100%' : 'auto',
                          touchAction: 'manipulation',
                          minHeight: isMobile ? '48px' : 'auto',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                        onMouseEnter={e => {
                          if (!loading) {
                            e.currentTarget.style.background = '#b91c1c';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseOut={e => {
                          if (!loading) {
                            e.currentTarget.style.background = '#dc2626';
                            e.currentTarget.style.transform = 'none';
                          }
                        }}
                      >
                        {loading ? (isArabic ? 'جارٍ الحذف...' : 'Deleting...') : t.confirmDelete}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Crop Modal */}
      {showCropModal && imageToCrop && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(4px)',
          padding: isMobile ? '16px' : '0'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: isMobile ? 16 : 20,
            padding: isMobile ? 16 : 24,
            maxWidth: isMobile ? '100%' : 600,
            width: isMobile ? '100%' : '90%',
            maxHeight: isMobile ? '95vh' : '90vh',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 16 : 20,
            overflowY: 'auto'
          }}>
            <div style={{ 
              fontSize: isMobile ? 20 : 24, 
              fontWeight: 700, 
              color: '#111827' 
            }}>
              {isArabic ? 'قص الصورة' : 'Crop Image'}
            </div>
            
            <div style={{
              position: 'relative',
              width: '100%',
              height: isMobile ? 280 : 400,
              background: '#000',
              borderRadius: 12,
              overflow: 'hidden',
              minHeight: isMobile ? 280 : 400
            }}>
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: {
                    width: '100%',
                    height: '100%',
                    position: 'relative'
                  },
                  cropAreaStyle: {
                    border: '2px solid #FF7426'
                  }
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 12 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? 8 : 12,
                flexWrap: isMobile ? 'wrap' : 'nowrap'
              }}>
                <label style={{ 
                  fontSize: isMobile ? 13 : 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  minWidth: isMobile ? 50 : 60 
                }}>
                  {isArabic ? 'التكبير:' : 'Zoom:'}
                </label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    height: isMobile ? 8 : 6,
                    borderRadius: 3,
                    background: '#e5e7eb',
                    outline: 'none',
                    cursor: 'pointer',
                    minWidth: isMobile ? 120 : 'auto'
                  }}
                />
                <span style={{ 
                  fontSize: isMobile ? 13 : 14, 
                  fontWeight: 600, 
                  color: '#6b7280', 
                  minWidth: isMobile ? 35 : 40 
                }}>
                  {Math.round(zoom * 100)}%
                </span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? 10 : 12, 
                justifyContent: 'flex-end',
                flexDirection: isMobile ? 'column-reverse' : 'row',
                width: '100%'
              }}>
                <button
                  type="button"
                  onClick={handleCropCancel}
                  disabled={loading}
                  style={{
                    padding: isMobile ? '12px 20px' : '12px 24px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 700,
                    fontSize: isMobile ? 14 : 15,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: loading ? 0.5 : 1,
                    width: isMobile ? '100%' : 'auto',
                    touchAction: 'manipulation',
                    minHeight: isMobile ? '48px' : 'auto',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onMouseEnter={e => {
                    if (!loading) {
                      e.currentTarget.style.background = '#e5e7eb';
                    }
                  }}
                  onMouseOut={e => {
                    if (!loading) {
                      e.currentTarget.style.background = '#f3f4f6';
                    }
                  }}
                >
                  {isArabic ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={handleCropComplete}
                  disabled={loading}
                  style={{
                    padding: isMobile ? '12px 20px' : '12px 24px',
                    background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 700,
                    fontSize: isMobile ? 14 : 15,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: loading ? 0.7 : 1,
                    boxShadow: '0 4px 16px rgba(255,116,38,0.3)',
                    width: isMobile ? '100%' : 'auto',
                    touchAction: 'manipulation',
                    minHeight: isMobile ? '48px' : 'auto',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onMouseEnter={e => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(255,116,38,0.4)';
                    }
                  }}
                  onMouseOut={e => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(255,116,38,0.3)';
                    }
                  }}
                >
                  {loading ? (isArabic ? 'جارٍ الحفظ...' : 'Saving...') : (isArabic ? 'حفظ' : 'Save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
} 
